import { ViewportScroller } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { debounceTime, filter, map, pairwise, startWith } from 'rxjs';

import {
  resetSearchBoxSearchJsonAndPagination,
  setSearchBoxSearchJson,
} from './shared-ui-search-box/data-access/store/search-box.actions';
@Component({
  template: ``,
})
export abstract class AbstractFilterComponent<
  T extends { [K in keyof T]: AbstractControl<any, any> },
> {
  store$ = inject(Store);
  activatedRoute = inject(ActivatedRoute);

  previousUrl = '';
  scrollPosition = 0;
  protected form: FormGroup;
  searchJson: any = {};
  queryParams = {};
  viewportScroller = inject(ViewportScroller);
  router = inject(Router);
  fb = inject(FormBuilder);
  destroyRef = inject(DestroyRef);

  constructor() {
    this.previousUrl = this.router.url.split('?')[0];
  }

  protected queryParamsToFilterForm(queryParams: Params): Partial<T> {
    const filterForm: any = {};

    for (const key in queryParams) {
      const value = queryParams[key];

      if (value === 'null') {
        filterForm[key] = null;
      } else if (value.includes(',')) {
        filterForm[key] = value
          .replace(/,\s*$/, '')
          .split(',')
          .filter((item: string) => item !== '')
          .map((item: string) => {
            const num = Number(item.trim());
            return isNaN(num) ? item.trim() : num;
          });
      } else if (value === 'true') {
        filterForm[key] = true;
      } else {
        filterForm[key] = value;
      }
    }
    return filterForm;
  }
  protected filterFormToQueryParams(filterForm: Partial<T>): Params {
    if (!filterForm) return {};
    return Object.keys(filterForm).reduce((acc, key) => {
      const value = (filterForm as any)[key];

      if (value === null || value === undefined) {
        return { ...acc, [key]: null };
      }

      return { ...acc, [key]: value };
    }, {});
  }
  protected subscribeToFormValueChanges() {
    if (this.form) {
      this.form.valueChanges
        .pipe(
          debounceTime(300),
          startWith(this.form.value),

          pairwise(),

          map(([oldState, newState]) => {
            const changes: any = {};
            for (const key in newState) {
              if (oldState[key] !== newState[key] && oldState[key] !== undefined) {
                changes[key] = newState[key];
              }
            }
            return changes;
          }),

          filter((changes) => Object.keys(changes).length !== 0 && !this.form.invalid),

          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (form: any) => {
            this.initSearchJson(this.filterFormToQueryParams(form));
          },
        });
    }
  }
  initSearchJson(formData: any) {
    const previousSearchJson = this.searchJson;

    this.form.patchValue(formData, { emitEvent: false });
    this.queryParams = { ...this.queryParams, ...formData };
    this.setSearchJson(this.queryParams);

    if (JSON.stringify(previousSearchJson) !== JSON.stringify(this.searchJson)) {
      this.store$.dispatch(
        setSearchBoxSearchJson({
          searchJson: this.searchJson,
        }),
      );
    }
  }
  resetForm() {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.patchValue('');
    });
  }

  resetStore() {
    this.store$.dispatch(resetSearchBoxSearchJsonAndPagination());
  }
  abstract setSearchJson(formData: any): void;
  abstract initForm(): void;
  onQueryChanged() {}
  changeParams(params: Params) {
    return params;
  }
}
